import { useState } from 'react';

import {
  useApi,
  discoveryApiRef,
  alertApiRef,
  DiscoveryApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import { OwnershipEntity, OwnershipHandlerResult } from '../types';

export interface PagerDutyTeamOwnershipItem {
  pd_team_id: string;
  team_id: string;
  owner_confirmed: boolean;
}

export interface PagerDutyTeamOwnershipHandlerOptions {
  /**
   * Whether the assignment counts as a human confirmation of the owner.
   * Confirming through the "Manage ownership" dialog is a confirmation, so this
   * defaults to true; pass false for flows that assign an owner without one.
   */
  ownerConfirmed?: boolean;
}

export interface PagerDutyTeamOwnershipRequest {
  updates: PagerDutyTeamOwnershipItem[];
}

export interface PagerDutyTeamOwnershipResponse {
  data: Array<{
    pd_team_id: string;
    team_id: string;
    owner_confirmed?: boolean;
    updated: boolean;
  }>;
  total_requested: number;
  total_updated: number;
  failed: Array<{ pd_team_id: string; error: string }>;
  generated_at: string;
}

async function updatePagerDutyTeamOwnership(
  discoveryApi: DiscoveryApi,
  request: PagerDutyTeamOwnershipRequest,
): Promise<PagerDutyTeamOwnershipResponse> {
  const baseUrl = await discoveryApi.getBaseUrl('watchtower');

  const response = await fetch(`${baseUrl}/pagerduty-teams/ownership`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(request),
  });

  // 207 is a partial-success response and is still parseable as JSON.
  if (!response.ok && response.status !== 207) {
    const errorText = await response.text();
    throw new Error(
      `Failed to update PagerDuty team ownership: ${response.status} ${response.statusText}. ${errorText}`,
    );
  }

  return response.json();
}

async function refreshPagerDutyTeams(
  discoveryApi: DiscoveryApi,
  pdTeamIds: string[],
): Promise<{ updatedCount: number }> {
  const baseUrl = await discoveryApi.getBaseUrl('utility-api');

  const response = await fetch(
    `${baseUrl}/actions/refresh-pagerduty-teams-ownership`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ pd_team_ids: pdTeamIds }),
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to refresh PagerDuty teams: ${response.status} ${response.statusText}. ${errorText}`,
    );
  }

  return response.json();
}

export function usePagerDutyTeamOwnershipHandler(
  options: PagerDutyTeamOwnershipHandlerOptions = {},
): OwnershipHandlerResult {
  const { ownerConfirmed = true } = options;
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const alertApi = useApi(alertApiRef);
  const [loading, setLoading] = useState(false);

  const submit = async (
    entities: OwnershipEntity[],
    ownerRef: string,
  ): Promise<boolean> => {
    setLoading(true);
    try {
      const fullTeamEntityRef = ownerRef.includes(':')
        ? ownerRef
        : `group:default/${ownerRef}`;

      const teamEntity = await catalogApi.getEntityByRef(fullTeamEntityRef);

      if (!teamEntity) {
        alertApi.post({
          message: `Team entity not found: ${fullTeamEntityRef}`,
          severity: 'error',
        });
        setLoading(false);
        return false;
      }

      const ownerTeamId =
        fullTeamEntityRef.split('/').pop()?.toUpperCase() || '';
      const teamName = teamEntity.metadata.title || teamEntity.metadata.name;

      const updates: PagerDutyTeamOwnershipItem[] = [];
      const pdTeamIds: string[] = [];

      for (const entity of entities) {
        const pdTeamId =
          entity.annotations?.['tomtom.com/pagerduty-team-id'] || entity.name;

        if (!pdTeamId) {
          alertApi.post({
            message: `PagerDuty team "${entity.name}" is missing pd_team_id`,
            severity: 'error',
          });
          setLoading(false);
          return false;
        }

        pdTeamIds.push(pdTeamId);
        updates.push({
          pd_team_id: pdTeamId,
          team_id: ownerTeamId,
          owner_confirmed: ownerConfirmed,
        });
      }

      const updateResult = await updatePagerDutyTeamOwnership(discoveryApi, {
        updates,
      });

      if (updateResult.failed.length > 0) {
        alertApi.post({
          message: `Failed to update ownership for ${
            updateResult.failed.length
          } PagerDuty teams: ${updateResult.failed
            .map(f => f.error)
            .join(', ')}`,
          severity: 'error',
        });
        setLoading(false);
        return false;
      }

      await refreshPagerDutyTeams(discoveryApi, pdTeamIds);

      const message =
        entities.length === 1
          ? `Successfully assigned ownership of PagerDuty team to ${teamName}`
          : `Successfully assigned ownership for ${entities.length} PagerDuty teams to ${teamName}`;

      alertApi.post({
        message,
        severity: 'success',
        display: 'transient',
      });

      setLoading(false);
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      alertApi.post({
        message: `Failed to update PagerDuty team ownership: ${errorMessage}`,
        severity: 'error',
      });
      setLoading(false);
      return false;
    }
  };

  return {
    submit,
    loading,
  };
}
